import { ReactNode, useState, useEffect, useCallback } from "react"
import { DesktopNav, MobileTabBar } from "./MainNav"
import { UserMenu } from "./UserMenu"
import { StatusIndicators } from "./StatusIndicators"
import { SearchOverlay } from "@/components/files/SearchOverlay"
import { X, Search, Lightbulb } from "lucide-react"

interface AppShellProps {
  children: ReactNode
  captureMode?: boolean
  onExitCapture?: () => void
}

export function AppShell({ children, captureMode = false, onExitCapture }: AppShellProps) {
  const [searchOpen, setSearchOpen] = useState(false)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // Don't trigger when user is typing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement
      const isEditable = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable
      // Don't trigger on editor pages (file viewing/editing)
      const isEditorPage = window.location.pathname.startsWith("/files/")
      if (!isEditable && !isEditorPage) {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
  }, [])

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  if (captureMode) {
    return (
      <div className="fixed inset-0 z-50 bg-background" data-testid="capture-overlay">
        <button
          onClick={onExitCapture}
          className="absolute left-4 top-4 z-10 rounded-full p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
          data-testid="exit-capture"
        >
          <X className="h-6 w-6" />
        </button>
        {children}
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Desktop top bar */}
      <header className="hidden md:flex h-14 items-center justify-between border-b border-border/60 bg-card px-6">
        <div className="flex items-center gap-4">
          <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Lightbulb className="h-[18px] w-[18px]" /></div>
          <span className="text-lg font-bold tracking-tight">Brain Down</span>
        </div>
        <DesktopNav />
        <div className="flex items-center gap-3">
          <StatusIndicators />
          <button
            onClick={() => setSearchOpen(true)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Search (/)"
          >
            <Search className="h-4 w-4" />
          </button>
          <UserMenu />
        </div>
      </header>

      {/* Mobile header */}
      <header className="flex md:hidden h-12 items-center justify-between border-b border-border/60 bg-card px-4">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Lightbulb className="h-4 w-4" /></div>
          <span className="text-base font-bold tracking-tight">Brain Down</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusIndicators />
          <button
            onClick={() => setSearchOpen(true)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Search"
          >
            <Search className="h-4 w-4" />
          </button>
          <UserMenu />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 pb-[calc(4rem+env(safe-area-inset-bottom,0px))] md:pb-0">
        {children}
      </main>

      {/* Mobile bottom tab bar */}
      <MobileTabBar />

      {/* Search modal */}
      {searchOpen && (
        <SearchOverlay onClose={() => setSearchOpen(false)} />
      )}
    </div>
  )
}
